import { Table, TableHeader, TableBody } from '../../../components/table';
import React, { useRef, useEffect, useState } from 'react'

import { Icon } from '@iconify/react';
import NoData from '../../../components/common/NoData';
import ContentUnavailable from '../../../components/common/ContentUnavailable';
import Loader from '../../../components/common/Loader';
import { Dialog } from '../../../components/ui';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { toast } from '../../../components/ui/notify';
import {
  addJsonSheet,
  createWorkbook,
  downloadWorkbook,
} from '../../../utils/excelWorkbook';
import { classroomDashboard, classroomVisibility } from '../../../services/organization/classroom/classroom.service';
import { getAssignmentsForClassroom, getClassroomAssignmentDetails } from '../../../services/organization/classroom/classroomAssignment.service';
import { getAllAssessmentToClassroom, getClassroomAssessmentById } from '../../../services/organization/classroom/classroomAssessments.service';
import { getZoomLiveClasses, getClassroomLiveClassById } from '../../../services/organization/classroom/classroomLiveclasses.service';
import { getClassroomResources, getClassroomResourceById } from '../../../services/organization/classroom/classroomResources.service';
import {
  getChannelMessagesApi,
  getChannelUnreadApi,
  markChannelReadApi,
} from '../../../services/getstream/getstream.service';
import ClassroomLiveClasses from './ClassroomLiveClasses';
import ClassroomAssessments from './ClassroomAssessments';
import ClassroomAssignments from './ClassroomAssignments';
import ClassroomCourses from './ClassroomCourses';
import ClassroomCommunity from '../../user/classroom/Cummunity';
import ClassroomRecorded from './ClassroomRecorded';

import { usePermissions } from '../../../context/PermissionsContext';
import { encodeData, decodeData } from '../../../utils/encodeAndEncode';

/** Community classroom tab - hidden until the feature is supported end-to-end */
const SHOW_CLASSROOM_COMMUNITY_TAB = true;

const VALID_DASHBOARD_TABS = ['courses', 'assignments', 'assessments', 'live', 'recorded', 'community'];

function getDashboardReportConfig(activeTab) {
  switch (activeTab) {
    case 'assignments':
      return {
        title: 'Assignments report',
        sheet: 'Assignments',
        columns: [
          { key: 'title', label: 'Assignment' },
          { key: 'description', label: 'Description' },
          { key: 'created_by_name', label: 'Created by' },
          { key: 'assigned_date', label: 'Assigned on' },
          { key: 'due_date', label: 'Due date' },
          { key: 'total_marks', label: 'Total marks' },
          { key: 'is_visible', label: 'Visible' },
        ],
      };
    case 'assessments':
      return {
        title: 'Assessments report',
        sheet: 'Assessments',
        columns: [
          { key: 'assessment_name', label: 'Assessment' },
          { key: 'duration', label: 'Duration' },
          { key: 'pass_percentage', label: 'Pass %' },
          { key: 'total_questions', label: 'Questions' },
          { key: 'completed_count', label: 'Attended' },
          { key: 'is_active', label: 'Active' },
          { key: 'is_visible', label: 'Visible' },
        ],
      };
    case 'live':
      return {
        title: 'Live classes report',
        sheet: 'Live Classes',
        columns: [
          { key: 'live_class_name', label: 'Class name' },
          { key: 'live_class_description', label: 'Description' },
          { key: 'start_time', label: 'Start time' },
          { key: 'duration', label: 'Duration' },
          { key: 'time_zone', label: 'Time zone' },
          { key: 'is_started', label: 'Started' },
          { key: 'is_ended', label: 'Ended' },
        ],
      };
    case 'recorded':
      return {
        title: 'Resources report',
        sheet: 'Resources',
        columns: [
          { key: 'resource_name', label: 'Resource' },
          { key: 'resource_description', label: 'Description' },
          { key: 'resource_type', label: 'Type' },
          { key: 'created_at', label: 'Created at' },
          { key: 'is_active', label: 'Active' },
          { key: 'media_url', label: 'URL' },
        ],
      };
    case 'community':
      return {
        title: 'Community report',
        sheet: 'Community',
        columns: [
          { key: 'sender', label: 'Sender' },
          { key: 'text', label: 'Message' },
          { key: 'createdAt', label: 'Created at' },
          { key: 'attachmentCount', label: 'Attachments' },
        ],
      };
    default:
      return {
        title: 'Report',
        sheet: 'Report',
        columns: [],
      };
  }
}

function buildDashboardReportWorkbook(activeTab, rows) {
  const config = getDashboardReportConfig(activeTab);
  const workbook = createWorkbook();
  const exportRows = (rows || []).map((row, index) => {
    const out = { No: index + 1 };
    config.columns.forEach(({ key, label }) => {
      let value = row[key];
      if (typeof value === 'boolean') value = value ? 'Yes' : 'No';
      if (key === 'is_visible' || key === 'is_active' || key === 'is_started' || key === 'is_ended') {
        value = Number(value) === 1 ? 'Yes' : 'No';
      }
      out[label] = value ?? '';
    });
    return out;
  });
  addJsonSheet(workbook, config.sheet, exportRows.length ? exportRows : [{ _note: 'No rows' }]);
  return workbook;
}

function ClassroomDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { permissions } = usePermissions();
  const { classroomId } = useParams();
  const decodedClassroomId = decodeData(classroomId);

  const urlActiveTab = searchParams.get('active-tab');
  const linkedAssignmentId = searchParams.get('assignment');
  const linkedAssessmentId = searchParams.get('assessment');
  const linkedLiveClassId = searchParams.get('liveClass');
  const linkedResourceId = searchParams.get('resource');
  const resolvedInitialTab = VALID_DASHBOARD_TABS.includes(urlActiveTab) ? urlActiveTab : 'assignments';
  const [activeTab, setActiveTab] = useState(resolvedInitialTab);
  const [tabRefreshKey, setTabRefreshKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dashboardStatus, setDashboardStatus] = useState('loading');
  const [linkedContentStatus, setLinkedContentStatus] = useState('idle');
  const [dashboardData, setDashboardData] = useState(null);
  const [trainers, setTrainers] = useState([]);
  const [groupData, setGroupData] = useState(null);
  const [classroomName, setClassroomName] = useState('');
  const [isTrainersExpanded, setIsTrainersExpanded] = useState(false);
  const [isTabSettingsOpen, setIsTabSettingsOpen] = useState(false);
  const [communityUnread, setCommunityUnread] = useState(0);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportRows, setReportRows] = useState([]);
  const [tabVisibility, setTabVisibility] = useState({
    assignments: true,
    assessments: true,
    live: true,
    recorded: true,
    community: true
  });
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  const patchSearchParams = (mutator) => {
    const next = new URLSearchParams(searchParams);
    mutator(next);
    setSearchParams(next, { replace: true });
  };

  // Backward-compatible tab access checks:
  // Some roles may have granular classroom permissions without dedicated *_view slugs.
  const canViewAssignments = Boolean(
    permissions.classroom_assignment_view ||
    permissions.classroom_assignment_manage ||
    permissions.classroom_assignment_create ||
    permissions.classroom_assignment_edit ||
    permissions.classroom_assignment_delete
  );
  const canViewAssessments = Boolean(
    permissions.classroom_assessment_view ||
    permissions.classroom_assessment_manage ||
    permissions.classroom_assessment_create ||
    permissions.classroom_assessment_edit ||
    permissions.classroom_assessment_delete ||
    permissions.classroom_assessment_question_view ||
    permissions.classroom_assessment_result_view
  );
  const canViewLiveClasses = Boolean(permissions.classroom_live_classes_view ?? permissions.classroom_liveclasses_view);
  const canViewResources = Boolean(permissions.classroom_resources_view);
  const canViewCourses = Boolean(permissions.classroom_courses_view ?? true);
  const canViewCommunity = Boolean(permissions.classroom_community_view);

  // Handle clicks outside the dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking inside dropdown or on the button
      if (dropdownRef.current?.contains(event.target) || buttonRef.current?.contains(event.target)) {
        return;
      }
      // Close if clicking outside
      if (isTabSettingsOpen) {
        setIsTabSettingsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isTabSettingsOpen]);

  // Mock function to simulate API call for tab visibility update
  const updateTabVisibility = async (e, tabName, isVisible) => {
    e.stopPropagation();

    try {
      // Create new state
      const newState = {
        ...tabVisibility,
        [tabName]: isVisible
      };

      // Generate payload for API
      const is_visibility = [
        {
          tab_name: "assignment",
          is_visible: newState.assignments.toString()
        },
        {
          tab_name: "assessment",
          is_visible: newState.assessments.toString()
        },
        {
          tab_name: "liveclass",
          is_visible: newState.live.toString()
        },
        {
          tab_name: "recorded",
          is_visible: newState.recorded.toString()
        },
        ...(SHOW_CLASSROOM_COMMUNITY_TAB
          ? [
              {
                tab_name: 'community',
                is_visible: newState.community.toString(),
              },
            ]
          : [{ tab_name: 'community', is_visible: 'false' }])
      ];

      // Prepare API payload
      const apiPayload = {
        classroom_id: parseInt(decodedClassroomId),
        is_visibility: is_visibility
      };

      // Update local state first (optimistic update)
      setTabVisibility(newState);

      // Call the API
      const response = await classroomVisibility(apiPayload);

      if (response.success) {
        toast.success('Tab visibility updated successfully');
      } else {
        // Revert state if API call fails
        setTabVisibility(tabVisibility);
        toast.error('Failed to update tab visibility');
      }
    } catch (error) {
      // Revert state if API call fails
      setTabVisibility(tabVisibility);
      console.error('Error updating tab visibility:', error);
      toast.error('Failed to update tab visibility');
    }
  };

  // Tab settings component
  const TabSettings = () => (
    <div
      ref={dropdownRef}
      className={"[&_label]:tw-cursor-pointer [&_label]:tw-p-[6px_8px] [&_label]:tw-rounded-[4px] [&_label]:[transition:background-color_0.2s] [&_label:hover]:tw-bg-[#f5f5f5] [&_input[type=\"checkbox\"]]:tw-cursor-pointer tw-border-[1px] tw-border-solid tw-border-[#e0e0e0] tw-animate-[fadeIn_0.2s_ease-in-out] tab-settings-dropdown tw-absolute tw-bg-white tw-shadow-sm tw-rounded-xl tw-p-4"}
      style={{ right: '0', top: '100%', zIndex: 1000, minWidth: '200px' }}
      onClick={(e) => e.stopPropagation()}
    >
      <h6 className="tw-mb-4">Tab Visibility</h6>
      <div className="tw-flex tw-flex-col tw-gap-2">
        <label className="tw-flex tw-items-center tw-gap-2" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={tabVisibility.assignments}
            onChange={(e) => updateTabVisibility(e, 'assignments', !tabVisibility.assignments)}
          />
          <span style={{ color: tabVisibility.assignments ? 'inherit' : '#666' }}>
            Assignments
          </span>
        </label>
        <label className="tw-flex tw-items-center tw-gap-2" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={tabVisibility.assessments}
            onChange={(e) => updateTabVisibility(e, 'assessments', !tabVisibility.assessments)}
          />
          <span style={{ color: tabVisibility.assessments ? 'inherit' : '#666' }}>
            Assessments
          </span>
        </label>
        <label className="tw-flex tw-items-center tw-gap-2" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={tabVisibility.live}
            onChange={(e) => updateTabVisibility(e, 'live', !tabVisibility.live)}
          />
          <span style={{ color: tabVisibility.live ? 'inherit' : '#666' }}>
            Live Classes
          </span>
        </label>
        <label className="tw-flex tw-items-center tw-gap-2" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={tabVisibility.recorded}
            onChange={(e) => updateTabVisibility(e, 'recorded', !tabVisibility.recorded)}
          />
          <span style={{ color: tabVisibility.recorded ? 'inherit' : '#666' }}>
            Resources
          </span>
        </label>
        {SHOW_CLASSROOM_COMMUNITY_TAB && (
          <label className="tw-flex tw-items-center tw-gap-2" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={tabVisibility.community}
              onChange={(e) => updateTabVisibility(e, 'community', !tabVisibility.community)}
            />
            <span style={{ color: tabVisibility.community ? 'inherit' : '#666' }}>
              Community
            </span>
          </label>
        )}
      </div>
    </div>
  );

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setDashboardStatus('loading');
      const response = await classroomDashboard({ classroom_id: decodedClassroomId });

      if (response && response.success && response.data) {
        const data = response.data;
        setDashboardData(data);
        setTrainers(data.trainers || []);
        setGroupData(data.group || null);
        if (data.cls_name) {
          setClassroomName(data.cls_name);
        }

        // Update tab visibility from API response
        if (data.is_visibility && Array.isArray(data.is_visibility)) {
          const newTabVisibility = {
            assignments: data.is_visibility.find(tab => tab.tab_name === 'assignment')?.is_visible === 'true',
            assessments: data.is_visibility.find(tab => tab.tab_name === 'assessment')?.is_visible === 'true',
            live: data.is_visibility.find(tab => tab.tab_name === 'liveclass')?.is_visible === 'true',
            recorded: data.is_visibility.find(tab => tab.tab_name === 'recorded')?.is_visible === 'true',
            community: data.is_visibility.find(tab => tab.tab_name === 'community')?.is_visible === 'true'
          };
          setTabVisibility(newTabVisibility);
        }
        setDashboardStatus('available');
      } else {
        setDashboardStatus('unavailable');
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setDashboardStatus('unavailable');
    } finally {
      setLoading(false);
    }
  };

  const markCommunitySeen = async (channelId) => {
    if (!channelId) return;
    try {
      await markChannelReadApi({ channelId });
    } catch (e) {
      // ignore
    }
    try {
      const uid = (() => {
        const u = JSON.parse(localStorage.getItem('user') || 'null');
        return u?.id ? String(u.id) : 'anon';
      })();
      localStorage.setItem(`community_last_seen:${uid}:${channelId}`, new Date().toISOString());
    } catch (e) {
      // ignore
    }
    setCommunityUnread(0);
  };

  const computeUnread = async (channelId) => {
    if (!channelId) return;
    const resp = await getChannelUnreadApi({ channelId });
    const unread = Number(resp?.data?.unread_count ?? 0) || 0;
    setCommunityUnread(unread);
  };

  const UNREAD_POLL_INTERVAL_MS = 30000;

  useEffect(() => {
    if (!SHOW_CLASSROOM_COMMUNITY_TAB) return;
    if (activeTab === 'community') return;
    const channelId = dashboardData?.stream_channel_id;
    if (!channelId) return;

    let timer = null;
    const tick = async () => {
      if (document.visibilityState === 'hidden') return;
      try {
        await computeUnread(channelId);
      } catch (e) {
        // ignore
      }
    };

    tick();
    timer = setInterval(tick, UNREAD_POLL_INTERVAL_MS);
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [dashboardData?.stream_channel_id, activeTab]);

  useEffect(() => {
    if (!SHOW_CLASSROOM_COMMUNITY_TAB || activeTab !== 'community') return;
    const channelId = dashboardData?.stream_channel_id;
    if (!channelId) return;
    markCommunitySeen(channelId);
  }, [activeTab, dashboardData?.stream_channel_id]);

  useEffect(() => {
    if (urlActiveTab && VALID_DASHBOARD_TABS.includes(urlActiveTab) && urlActiveTab !== activeTab) {
      setActiveTab(urlActiveTab);
    }
  }, [urlActiveTab, activeTab]);

  useEffect(() => {
    if (!searchParams.get('active-tab')) {
      patchSearchParams((next) => {
        next.set('active-tab', activeTab);
      });
    }
  }, [searchParams, activeTab]);

  useEffect(() => {
    if (linkedAssignmentId) {
      if (activeTab !== 'assignments') {
        setActiveTab('assignments');
        patchSearchParams((next) => {
          next.set('active-tab', 'assignments');
        });
      }
    } else if (linkedAssessmentId) {
      if (activeTab !== 'assessments') {
        setActiveTab('assessments');
        patchSearchParams((next) => {
          next.set('active-tab', 'assessments');
        });
      }
    } else if (linkedLiveClassId) {
      if (activeTab !== 'live') {
        setActiveTab('live');
        patchSearchParams((next) => {
          next.set('active-tab', 'live');
        });
      }
    } else if (linkedResourceId) {
      if (activeTab !== 'recorded') {
        setActiveTab('recorded');
        patchSearchParams((next) => {
          next.set('active-tab', 'recorded');
        });
      }
    }
  }, [linkedAssignmentId, linkedAssessmentId, linkedLiveClassId, linkedResourceId]);

  useEffect(() => {
    if (dashboardStatus !== 'available' || !decodedClassroomId) {
      return undefined;
    }

    if (!linkedAssignmentId && !linkedAssessmentId && !linkedLiveClassId && !linkedResourceId) {
      setLinkedContentStatus('idle');
      return undefined;
    }

    let cancelled = false;

    const verifyLinkedContent = async () => {
      setLinkedContentStatus('loading');
      try {
        if (linkedAssignmentId) {
          const response = await getClassroomAssignmentDetails({
            class_id: decodedClassroomId,
            assignment_id: linkedAssignmentId,
          });
          if (response?.success && response?.data?.id) {
            if (!cancelled) setLinkedContentStatus('available');
            return;
          }
        }

        if (linkedAssessmentId) {
          const response = await getClassroomAssessmentById({
            class_id: decodedClassroomId,
            assessment_id: linkedAssessmentId,
          });
          if (response?.success && response?.data?.assessment) {
            if (!cancelled) setLinkedContentStatus('available');
            return;
          }
        }

        if (linkedLiveClassId) {
          const response = await getClassroomLiveClassById({
            classroom_id: decodedClassroomId,
            live_class_id: linkedLiveClassId,
          });
          if (response?.success && response?.data?.liveClass) {
            if (!cancelled) setLinkedContentStatus('available');
            return;
          }
        }

        if (linkedResourceId) {
          const response = await getClassroomResourceById({
            classroom_id: decodedClassroomId,
            resource_id: linkedResourceId,
          });
          if (response?.success && response?.data?.resource) {
            if (!cancelled) setLinkedContentStatus('available');
            return;
          }
        }

        if (!cancelled) setLinkedContentStatus('unavailable');
      } catch (error) {
        if (!cancelled) setLinkedContentStatus('unavailable');
      }
    };

    verifyLinkedContent();

    return () => {
      cancelled = true;
    };
  }, [dashboardStatus, decodedClassroomId, linkedAssignmentId, linkedAssessmentId, linkedLiveClassId, linkedResourceId]);

  const getLinkedUnavailableContent = () => {
    if (linkedAssignmentId) {
      return {
        title: 'This assignment is not available',
        icon: 'mdi:file-document-remove-outline',
      };
    }
    if (linkedAssessmentId) {
      return {
        title: 'This assessment is not available',
        icon: 'mdi:clipboard-remove-outline',
      };
    }
    if (linkedLiveClassId) {
      return {
        title: 'This live class is not available',
        icon: 'mdi:video-off-outline',
      };
    }
    if (linkedResourceId) {
      return {
        title: 'This resource is not available',
        icon: 'mdi:folder-remove-outline',
      };
    }
    return {
      title: 'This content is not available',
      icon: 'mdi:alert-circle-outline',
    };
  };

  const handleDismissLinkedContent = () => {
    patchSearchParams((next) => {
      next.delete('assignment');
      next.delete('assessment');
      next.delete('liveClass');
      next.delete('resource');
    });
    setLinkedContentStatus('idle');
  };

  useEffect(() => {
    if (!decodedClassroomId) {
      setDashboardStatus('unavailable');
      setLoading(false);
      return;
    }

    fetchDashboardData();
    setTabRefreshKey((key) => key + 1);
  }, [decodedClassroomId, location.key]);

  useEffect(() => {
    if (!SHOW_CLASSROOM_COMMUNITY_TAB && activeTab === 'community') {
      setActiveTab('assignments');
    }
  }, [activeTab]);

  useEffect(() => {
  }, [classroomName]);

  const handleTabChange = (tab) => {
    // Check permissions before changing tabs
    switch(tab) {
      case 'courses':
        if (!canViewCourses) {
          toast.warning("You don't have permission to view courses");
          return;
        }
        break;
      case 'assignments':
        if (!canViewAssignments) {
          toast.warning("You don't have permission to view assignments");
          return;
        }
        break;
      case 'assessments':
        if (!canViewAssessments) {
          toast.warning("You don't have permission to view assessments");
          return;
        }
        break;
      case 'live':
        if (!canViewLiveClasses) {
          toast.warning("You don't have permission to view live classes");
          return;
        }
        break;
      case 'recorded':
        if (!canViewResources) {
          toast.warning("You don't have permission to view resources");
          return;
        }
        break;
      case 'community':
        if (!SHOW_CLASSROOM_COMMUNITY_TAB) {
          return;
        }
        if (!canViewCommunity) {
          toast.warning("You don't have permission to view community");
          return;
        }
        break;
      default:
        break;
    }
    setActiveTab(tab);
    patchSearchParams((next) => {
      next.set('active-tab', tab);
      next.delete('assignment');
      next.delete('assessment');
      next.delete('liveClass');
      next.delete('resource');
    });
    setLinkedContentStatus('idle');
  };

  const handleAddTrainee = () => {
    if (!permissions.classroom_trainees_view) {
      toast.warning("You don't have permission to view or add trainees");
      return;
    }
    let encodedClassroomId = encodeData(decodedClassroomId);
    navigate(`/admin/classrooms/classroom-dashboard/${encodedClassroomId}/trainees/${encodedClassroomId}?active-tab=${activeTab}`);
  };

  const canRunActiveTabReport = (() => {
    switch (activeTab) {
      case 'assignments':
        return canViewAssignments;
      case 'courses':
        return canViewCourses;
      case 'assessments':
        return canViewAssessments;
      case 'live':
        return canViewLiveClasses;
      case 'recorded':
        return canViewResources;
      case 'community':
        return SHOW_CLASSROOM_COMMUNITY_TAB && canViewCommunity;
      default:
        return false;
    }
  })();

  const handleCloseReportModal = () => {
    setShowReportModal(false);
    setReportRows([]);
  };

  const handleReportDownload = async () => {
    const workbook = buildDashboardReportWorkbook(activeTab, reportRows);
    const slug = `${classroomName || 'classroom'}-${activeTab}`
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 48) || 'classroom-report';
    await downloadWorkbook(workbook, `${slug}-report-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const handleReportClick = async () => {
    if (!canRunActiveTabReport) {
      toast.warning("You don't have permission to run this report");
      return;
    }

    setReportLoading(true);
    try {
      let rows = [];

      if (activeTab === 'assignments') {
        const response = await getAssignmentsForClassroom({
          class_id: decodedClassroomId,
          page: 1,
          limit: 1000,
          search: '',
        });
        rows = response?.data?.assignments || [];
      } else if (activeTab === 'assessments') {
        const response = await getAllAssessmentToClassroom({
          class_id: decodedClassroomId,
          page: 1,
          limit: 1000,
          search: '',
          startDate: '',
          endDate: '',
        });
        rows = response?.data?.assessments || [];
      } else if (activeTab === 'live') {
        const response = await getZoomLiveClasses({
          classroom_id: decodedClassroomId,
          page: 1,
          limit: 1000,
          search: '',
          status: '',
        });
        rows = response?.data?.liveClasses || response?.data?.live_classes || [];
      } else if (activeTab === 'recorded') {
        const response = await getClassroomResources({
          classroom_id: parseInt(decodedClassroomId, 10),
          page: 1,
          limit: 1000,
          resource_type: null,
        });
        rows = response?.data?.resources || [];
      } else if (activeTab === 'community') {
        const channelId = dashboardData?.stream_channel_id;
        if (!channelId) {
          toast.warning('Community channel is not available for this classroom');
          return;
        }
        const response = await getChannelMessagesApi({ channelId, limit: 1000, offset: 0 });
        rows = (response?.data?.messages || []).map((msg) => ({
          id: msg.id,
          sender: msg.user?.name || 'User',
          text: msg.text || '',
          createdAt: msg.created_at || '',
          attachmentCount: Array.isArray(msg.attachments) ? msg.attachments.length : 0,
        }));
      }

      setReportRows(rows);
      setShowReportModal(true);
    } catch (error) {
      const message =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to load report';
      toast.error(message);
    } finally {
      setReportLoading(false);
    }
  };

  const toggleTrainers = () => {
    setIsTrainersExpanded(!isTrainersExpanded);
  };

  if (loading || dashboardStatus === 'loading') {
    return <Loader variant="section" caption="Loading classroom..." />;
  }

  if (dashboardStatus === 'unavailable') {
    return (
      <ContentUnavailable
        title="This classroom is not available"
        description="This classroom may have been deleted or removed. It is no longer accessible."
        icon="mdi:school-off-outline"
        backLabel="Back to Classrooms"
        backTo="/admin/classrooms"
      />
    );
  }

  const reportConfig = getDashboardReportConfig(activeTab);

  return (
    <>
      <Dialog show={showReportModal} onHide={handleCloseReportModal} size="xl" centered scrollable>
        <Dialog.Header closeButton className="tw-border-b">
          <Dialog.Title>{reportConfig.title}</Dialog.Title>
        </Dialog.Header>
        <Dialog.Body className="tw-p-0">
          <p className="!tw-text-[var(--secondary-color)] !tw-text-[12px] tw-text-slate-500 tw-text-sm tw-px-4 tw-pt-4 tw-mb-2">
            {reportRows.length} row(s) in this report. Preview matches the Excel export.
          </p>
          <div className="tw-w-full tw-overflow-x-auto tw-rounded-[0.75rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [-webkit-overflow-scrolling:touch] ui-table-scroll tw-px-4 tw-pb-4" style={{ maxHeight: 'min(70vh, 560px)', overflow: 'auto' }}>
            <Table
              className={"[&_.deleted-accounts-actions_.ui-button-sm]:!tw-w-auto [&_.deleted-accounts-actions_.ui-button-sm]:!tw-h-auto [&_.deleted-accounts-actions_.ui-button-sm]:!tw-min-h-[32px] [&_.deleted-accounts-actions_.ui-button-sm]:!tw-p-[0.45rem_1.25rem] [&_.deleted-accounts-actions_.ui-button-sm]:!tw-inline-flex [&_.deleted-accounts-actions_.ui-button-sm]:!tw-items-center [&_.deleted-accounts-actions_.ui-button-sm]:!tw-justify-center [&_.deleted-accounts-actions_.btn-view]:!tw-min-w-[36px] [&_.deleted-accounts-actions_.btn-view]:!tw-p-[0.45rem_0.7rem] [&_.deleted-accounts-actions_.btn-restore]:!tw-min-w-[92px] [&_.deleted-accounts-actions_.btn-restore]:!tw-pl-[1.35rem] [&_.deleted-accounts-actions_.btn-restore]:!tw-pr-[1.35rem] [&_.deleted-accounts-actions_.btn-delete]:!tw-min-w-[92px] [&_.deleted-accounts-actions_.btn-delete]:!tw-pl-[1.35rem] [&_.deleted-accounts-actions_.btn-delete]:!tw-pr-[1.35rem] tw-w-full tw-border-collapse tw-text-left tw-text-[0.875rem] tw-leading-[1.25rem] [&_>_thead]:[--tw-bg-opacity:1] [&_>_thead]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&_th]:tw-whitespace-nowrap [&_th]:tw-border-b-[1px] [&_th]:tw-text-[0.75rem] [&_th]:tw-leading-[1rem] [&_th]:tw-font-semibold [&_th]:[--tw-text-opacity:1] [&_th]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] [&_td]:tw-border-b-[1px] [&_td]:tw-align-middle [&_td]:[--tw-text-opacity:1] [&_td]:tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] [&_tbody_tr:last-child_td]:tw-border-b-[0px] [&_tbody_tr]:[transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [&_tbody_tr]:[transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [&_tbody_tr]:[transition-duration:150ms] [&_tbody_tr:hover]:[--tw-bg-opacity:1] [&_tbody_tr:hover]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&_th]:[border-inline-end-width:1px] [&_th]:[--tw-border-opacity:1] [&_th]:tw-border-[rgb(241_245_249_/_var(--tw-border-opacity,_1))] [&_td]:[border-inline-end-width:1px] [&_td]:[--tw-border-opacity:1] [&_td]:tw-border-[rgb(241_245_249_/_var(--tw-border-opacity,_1))] [&_th]:tw-pl-3 [&_th]:tw-pr-3 [&_th]:tw-pt-2 [&_th]:tw-pb-2 [&_td]:tw-pl-3 [&_td]:tw-pr-3 [&_td]:tw-pt-2 [&_td]:tw-pb-2 ui-table ui-table-sm ui-table-bordered tw-align-middle tw-mb-0"}
              style={{ width: 'max-content', minWidth: '100%' }}
            >
              <TableHeader className="[--tw-bg-opacity:1] tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] ui-table-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <tr>
                  <th className="tw-whitespace-nowrap">No.</th>
                  {reportConfig.columns.map((col) => (
                    <th key={col.key} className="tw-whitespace-nowrap">{col.label}</th>
                  ))}
                </tr>
              </TableHeader>
              <TableBody>
                {reportRows.length === 0 ? (
                  <tr>
                    <td colSpan={reportConfig.columns.length + 1} className="tw-p-0">
                      <NoData message="No data available" />
                    </td>
                  </tr>
                ) : (
                  reportRows.map((row, index) => (
                    <tr key={row.id || row.assessment_id || row.live_class_id || `${activeTab}-${index}`}>
                      <td className="tw-whitespace-nowrap">{index + 1}</td>
                      {reportConfig.columns.map((col) => {
                        let value = row[col.key];
                        if (col.key === 'is_visible' || col.key === 'is_active' || col.key === 'is_started' || col.key === 'is_ended') {
                          value = Number(value) === 1 ? 'Yes' : 'No';
                        }
                        return <td key={col.key} className="tw-whitespace-nowrap">{value ?? '-'}</td>;
                      })}
                    </tr>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Dialog.Body>
        <Dialog.Footer className="tw-border-t">
          <button type="button" className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-p-[8px_16px] tw-text-[0.875rem] tw-font-medium tw-rounded-[0.5rem] [transition:all_0.2s_ease] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[1px] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] ui-button ui-button-light"} onClick={handleCloseReportModal}>
            Close
          </button>
          <button type="button" className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-text-[0.875rem] tw-rounded-[0.5rem] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[transparent] tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] ui-button ui-button-primary"} onClick={handleReportDownload}>
            <Icon icon="fluent:arrow-download-24-regular" width="18" height="18" className="tw-me-1" />
            Download Excel
          </button>
        </Dialog.Footer>
      </Dialog>

      <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row tw-mb-6"}>
        <div className="max-[768px]:!tw-w-[50%] tw-min-w-[0px] ui-column md:tw-w-1/2">
          <div className={"[&_h5]:tw-mb-0 [&_h5]:[font-weight:var(--font-weight-semibold)] [&_h5]:tw-text-[var(--text-primary)] [&_h5]:tw-flex [&_h5]:tw-items-center [&_h5]:tw-gap-[var(--spacing-sm)] [&_h6]:tw-mb-0 [&_h6]:[font-weight:var(--font-weight-semibold)] [&_h6]:tw-text-[var(--text-primary)] [&_h6]:tw-flex [&_h6]:tw-items-center [&_h6]:tw-gap-[var(--spacing-sm)] [&_h5_.iconify]:tw-text-[1.25rem] [&_h5_.iconify]:tw-text-[var(--primary-color)] [&_h6_.iconify]:tw-text-[1.25rem] [&_h6_.iconify]:tw-text-[var(--primary-color)] [[data-theme='dark']_&]:tw-border-[var(--border-color)] max-[768px]:tw-p-[var(--spacing-sm)_var(--spacing-md)] tw-border-solid tw-p-[var(--spacing-md)_var(--spacing-lg)] tw-rounded-tl-[0.75rem] tw-rounded-tr-[0.75rem] [--tw-border-opacity:1] tw-border-[rgb(241_245_249_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-pl-5 tw-pr-5 tw-pt-4 tw-pb-4 ui-card-header tw-bg-white tw-border-b-0 tw-flex tw-justify-between tw-items-center"}>
            <div>
              <div className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-mb-0">
                {classroomName ? (
                  <h4 className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-mb-0">{classroomName}</h4>
                ) : (
                  <small>(No classroom name available)</small>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="max-[768px]:!tw-w-[50%] tw-min-w-[0px] ui-column md:tw-w-1/2 tw-flex tw-justify-end tw-items-center">
          <div className="tw-flex tw-items-center tw-gap-2">
            <button
              className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-text-[0.875rem] tw-rounded-[0.5rem] tw-shrink-[0] tw-justify-center tw-border-[transparent] tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] ui-button ui-button-primary tw-flex tw-items-center tw-gap-2"}
              onClick={handleReportClick}
              disabled={reportLoading || !canRunActiveTabReport}
              title={!canRunActiveTabReport ? "You don't have permission to run this report" : `Report for ${activeTab}`}
            >
              {reportLoading ? (
                <>
                  <span className="tw-text-[#3152e8] tw-inline-block tw-shrink-[0] tw-animate-[tw-spin_1s_linear_infinite] tw-rounded-full tw-border-[2px] tw-border-[currentColor] tw-border-r-[transparent] tw-w-[1em] tw-h-[1em] ui-spinner ui-spinner-sm" role="status" aria-hidden="true" />
                  Loading...
                </>
              ) : (
                <>
                  <Icon icon="fluent:document-data-24-regular" width="18" height="18" />
                  Report
                </>
              )}
            </button>
            <button
              className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-text-[0.875rem] tw-rounded-[0.5rem] tw-shrink-[0] tw-justify-center tw-border-[transparent] tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] ui-button ui-button-primary tw-flex tw-items-center tw-gap-2"}
              style={{ width: '200px' }}
              onClick={handleAddTrainee}
              disabled={!permissions.classroom_trainees_view}
              title={!permissions.classroom_trainees_view ? "You don't have permission to view or add trainees" : "Add Trainees"}
            >
              <Icon icon="fluent:people-add-24-regular" width="20" height="20" />
              Add Trainees
            </button>
          </div>
        </div>
      </div>

      {/* Trainers Section as Dropdown */}
      <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row tw-mb-6"}>
        <div className="tw-min-w-[0px] ui-column tw-w-full">
          <div className={"[&_.collapse]:[transition:all_0.3s_ease-in-out] [&_.ui-card-header]:[transition:background-color_0.2s_ease] [&_.ui-card-header:hover]:!tw-bg-[#f8f9fa] [&_.ui-card-header_Icon]:[transition:transform_0.3s_ease] [&_.collapse.show_+_.ui-card-header_Icon]:[transform:rotate(180deg)] [&_>_.ui-card-header.permission-toolbar]:tw-sticky [&_>_.ui-card-header.permission-toolbar]:tw-top-[var(--permissions-header-offset,_0px)] [&_>_.ui-card-header.permission-toolbar]:tw-z-[10] [&_>_.ui-card-header.permission-toolbar]:[background:var(--bg-primary,_#fff)] [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[1px] [&_>_.ui-card-header.permission-toolbar]:tw-border-solid [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[var(--border-color,_#dee2e6)] [&_>_.ui-card-header.permission-toolbar]:[background-clip:border-box] [&_>_.ui-card-header.permission-toolbar]:tw-shadow-[0_3px_6px_rgba(0,_0,_0,_0.06)] [&.card-hover-primary:hover]:tw-border-[var(--primary-color)] [&.card-hover-success:hover]:tw-border-[var(--success-color)] [&.card-hover-danger:hover]:tw-border-[var(--danger-color)] [[data-theme='dark']_&]:tw-bg-[var(--bg-primary)] [[data-theme='dark']_&]:tw-border-[var(--border-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] tw-border-solid tw-rounded-[0.75rem] [transition:all_0.3s_ease-in-out] tw-relative tw-flex tw-min-w-[0px] tw-flex-col tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-shadow:0_2px_8px_-4px_rgb(15_23_42_/_.12)] [--tw-shadow-colored:0_2px_8px_-4px_var(--tw-shadow-color)] ui-card trainers-dropdown"}>
            <div
              className={"!tw-cursor-pointer [&_h5]:tw-mb-0 [&_h5]:[font-weight:var(--font-weight-semibold)] [&_h5]:tw-text-[var(--text-primary)] [&_h5]:tw-flex [&_h5]:tw-items-center [&_h5]:tw-gap-[var(--spacing-sm)] [&_h6]:tw-mb-0 [&_h6]:[font-weight:var(--font-weight-semibold)] [&_h6]:tw-text-[var(--text-primary)] [&_h6]:tw-flex [&_h6]:tw-items-center [&_h6]:tw-gap-[var(--spacing-sm)] [&_h5_.iconify]:tw-text-[1.25rem] [&_h5_.iconify]:tw-text-[var(--primary-color)] [&_h6_.iconify]:tw-text-[1.25rem] [&_h6_.iconify]:tw-text-[var(--primary-color)] [[data-theme='dark']_&]:tw-border-[var(--border-color)] max-[768px]:tw-p-[var(--spacing-sm)_var(--spacing-md)] tw-border-solid tw-p-[var(--spacing-md)_var(--spacing-lg)] tw-rounded-tl-[0.75rem] tw-rounded-tr-[0.75rem] tw-border-b-[1px] [--tw-border-opacity:1] tw-border-[rgb(241_245_249_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-pl-5 tw-pr-5 tw-pt-4 tw-pb-4 ui-card-header tw-bg-white tw-flex tw-justify-between tw-items-center cursor-pointer"}
              role="button"
              tabIndex={0}
              aria-expanded={isTrainersExpanded}
              aria-controls="classroom-trainers-list"
              onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); toggleTrainers(); } }}
              onClick={toggleTrainers}
              style={{ cursor: 'pointer' }}
            >
              <h5 className="tw-mb-0 tw-flex tw-items-center">
                <Icon icon="fluent:people-team-24-regular" className="tw-me-2" />
                Classroom Trainers
                <span className="!tw-text-[var(--secondary-color)] tw-ms-2 tw-text-slate-500">({trainers.length})</span>
              </h5>
              <Icon
                icon={isTrainersExpanded ? "fluent:chevron-up-24-regular" : "fluent:chevron-down-24-regular"}
                width="24"
                height="24"
                className="!tw-text-[var(--secondary-color)] tw-text-slate-500"
              />
            </div>
            <div id="classroom-trainers-list" hidden={!isTrainersExpanded}>
              <div className="max-[768px]:tw-p-[var(--spacing-md)] tw-p-5 tw-text-[var(--text-primary)] [flex:1_1_auto] ui-card-body">
                <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row"}>
                  {trainers.length === 0 && (
                    <div className="tw-min-w-[0px] ui-column tw-w-full">
                      <div className="tw-text-center">
                        <Icon icon="fluent:people-team-24-regular" className="tw-me-2" />
                        No trainers added yet
                      </div>
                    </div>
                  )}
                  {/* when there is no trainer show no trainee added yet */}
                  {trainers.map((trainer, index) => (
                    <div key={trainer.user_id} className="max-[768px]:!tw-w-[50%] tw-min-w-[0px] ui-column md:tw-w-1/2 lg:tw-w-1/3 tw-mb-4">
                      <div className={"[&.tw-border-black:hover]:tw-bg-[rgba(0,_0,_0,_0.05)] [[data-theme='dark']_&.tw-border-black]:!tw-border-[var(--border-color)] [[data-theme='dark']_&.tw-border-black]:!tw-text-[var(--text-primary)] [[data-theme='dark']_&.tw-border-black:hover]:tw-bg-[var(--bg-tertiary)] tw-bg-[#fff] [transition:all_0.3s_ease] tw-shadow-[0_2px_4px_rgba(0,0,0,0.05)] [&:hover]:[transform:translateY(-2px)] [&:hover]:tw-shadow-[0_4px_8px_rgba(0,0,0,0.1)] [&:hover_.trainer-avatar_img]:tw-border-[#0d6efd] [&:hover_.trainer-avatar_div]:tw-border-[#0d6efd] trainer-card tw-p-4 tw-border tw-rounded-xl tw-h-full"}>
                        <div className="tw-flex tw-items-center tw-mb-4">
                          <div className={"[&_img]:tw-border-[2px] [&_img]:tw-border-solid [&_img]:tw-border-[#e9ecef] [&_img]:[transition:all_0.3s_ease] [&_div]:tw-border-[2px] [&_div]:tw-border-solid [&_div]:tw-border-[#e9ecef] [&_div]:[transition:all_0.3s_ease] trainer-avatar tw-me-4"}>
                            {trainer.profile_pic_url ? (
                              <img
                                src={trainer.profile_pic_url}
                                alt={trainer.name}
                                className="tw-rounded-full"
                                style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                              />
                            ) : (
                              <div
                                className="tw-rounded-full tw-bg-slate-50 tw-flex tw-items-center tw-justify-center"
                                style={{ width: '60px', height: '60px' }}
                              >
                                <Icon icon="fluent:person-24-regular" width="30" height="30" />
                              </div>
                            )}
                          </div>
                          <div>
                            <h6 className="tw-mb-1">{trainer.name}</h6>
                            <span className={"!tw-text-[var(--primary-color)] [&.tw-bg-brand-50]:!tw-bg-[#e7f1ff] [&.tw-bg-brand-50]:tw-border-[1px] [&.tw-bg-brand-50]:tw-border-solid [&.tw-bg-brand-50]:tw-border-[#b6d4fe] tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-pl-2.5 tw-pr-2.5 tw-pt-1 tw-pb-1 tw-text-[0.75rem] tw-font-medium tw-leading-[1rem] ui-badge tw-bg-brand-50 tw-text-brand-600"}>{trainer.role}</span>
                          </div>
                        </div>
                        <div className={"[&_a]:tw-text-[#495057] [&_a]:[transition:color_0.2s_ease] [&_a:hover]:tw-text-[#0d6efd] trainer-details"}>
                          <div className="tw-mb-2">
                            <Icon icon="fluent:mail-24-regular" className="tw-me-2" />
                            <a href={`mailto:${trainer.email}`} className="tw-no-underline">
                              {trainer.email}
                            </a>
                          </div>
                          <div>
                            <Icon icon="fluent:phone-24-regular" className="tw-me-2" />
                            <a href={`tel:${trainer.phone}`} className="tw-no-underline">
                              {trainer.phone}
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row"}>
        <div className="max-[768px]:tw-p-[var(--spacing-md)] tw-text-[var(--text-primary)] [flex:1_1_auto] ui-card-body tw-p-0">
          <div className="tw-border-solid tw-border-b-[#e0e0e0] tw-bg-[#f8f9fa] tab-header tw-border-b tw-relative">
            <div className="tw-flex tw-justify-between tw-items-center">
              <ul className={"max-[767.98px]:[&[aria-label=\"breadcrumb\"]]:!tw-p-[0.75rem_1rem] max-[767.98px]:[&[aria-label=\"breadcrumb\"]_h4]:tw-text-[1.1rem] tw-flex tw-gap-1 [list-style:none] [&_.ui-nav-item]:tw-m-0 [&_.ui-nav-link:hover]:tw-text-[#3152e8] [&_.ui-nav-link:hover]:tw-border-0 [&_.ui-nav-link.active::after]:[content:''] [&_.ui-nav-link.active::after]:tw-absolute [&_.ui-nav-link.active::after]:tw-bottom-0 [&_.ui-nav-link.active::after]:tw-left-0 [&_.ui-nav-link.active::after]:tw-w-full [&_.ui-nav-link.active::after]:tw-h-[2px] [&_.ui-nav-link.active::after]:tw-bg-[#3152e8] max-[768px]:tw-p-[0_10px] max-[768px]:[&_.ui-nav-link]:tw-p-[12px_15px] max-[768px]:[&_.ui-nav-link]:tw-text-[14px] [&_.ui-nav-link.disabled]:!tw-cursor-not-allowed [&_.ui-nav-link.disabled]:![pointer-events:all] [&_.ui-nav-link.disabled]:tw-opacity-[0.6] [&_button.disabled]:!tw-cursor-not-allowed [&_button.disabled]:![pointer-events:all] [&_button.disabled]:tw-opacity-[0.6] [&_button[disabled]]:!tw-cursor-not-allowed [&_button[disabled]]:![pointer-events:all] [&_button[disabled]]:tw-opacity-[0.6] [&_.ui-nav-link.active]:tw-text-[#3152e8] [&_.ui-nav-link.active]:tw-border-0 [&_.ui-nav-link.active]:tw-rounded-br-[0px] [&_.ui-nav-link.active]:tw-rounded-bl-[0px] [&_.ui-nav-link.active]:tw-border-b-[2px] [&_.ui-nav-link.active]:[--tw-border-opacity:1] [&_.ui-nav-link.active]:tw-border-[rgb(64_84_214_/_var(--tw-border-opacity,_1))] [&_.ui-nav-link.active]:tw-bg-[transparent] tw-border-0 tw-p-[0_20px] tw-mb-5 tw-border-b-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] tw-overflow-x-auto tw-flex-nowrap [scrollbar-width:thin] [&_.ui-nav-link]:tw-border-0 [&_.ui-nav-link]:tw-text-[#6c757d] [&_.ui-nav-link]:tw-p-[15px_25px] [&_.ui-nav-link]:tw-font-medium [&_.ui-nav-link]:[transition:all_0.3s_ease] [&_.ui-nav-link]:tw-relative [&_.ui-nav-link]:tw-whitespace-nowrap ui-nav ui-nav-tabs"}>
                <li className={"tw-rounded-[8px] tw-m-[4px_8px] [font-family:var(--font-primary)] [font-weight:var(--font-weight-medium)] tw-text-[#555] [transition:all_0.2s_ease] tw-no-underline tw-relative [&:hover]:tw-bg-[rgba(13,_110,_253,_0.05)] [&:hover]:tw-text-[#0d6efd] [&.active]:tw-bg-[var(--primary-color)] [&.active]:tw-text-[white] [&.tw-text-rose-600]:tw-text-[#dc3545] [&.tw-text-rose-600:hover]:tw-bg-[rgba(220,_53,_69,_0.05)] [&.tw-text-rose-600:hover]:tw-text-[#dc3545] ui-nav-item"}>
                  <button
                    className={`tw-gap-2 tw-rounded-[0.5rem] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-text-[0.875rem] tw-leading-[1.25rem] tw-font-medium [--tw-text-opacity:1] tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:hover]:[--tw-text-opacity:1] [&:hover]:tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [&.active]:[--tw-bg-opacity:1] [&.active]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&.active]:[--tw-text-opacity:1] [&.active]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] ui-nav-link tw-flex tw-items-center   ${activeTab === "assignments" ? "active" : ""}      ${!canViewAssignments ? "disabled" : ""}`}
                    onClick={() => handleTabChange('assignments')}
                    disabled={!canViewAssignments}
                    title={!canViewAssignments ? "You don't have permission to view assignments" : "View Assignments"}
                  >
                    <Icon icon="fluent:clipboard-task-24-regular" className="tw-me-2" width="20" height="20" />
                    Assignments
                  </button>
                </li>
                <li className={"tw-rounded-[8px] tw-m-[4px_8px] [font-family:var(--font-primary)] [font-weight:var(--font-weight-medium)] tw-text-[#555] [transition:all_0.2s_ease] tw-no-underline tw-relative [&:hover]:tw-bg-[rgba(13,_110,_253,_0.05)] [&:hover]:tw-text-[#0d6efd] [&.active]:tw-bg-[var(--primary-color)] [&.active]:tw-text-[white] [&.tw-text-rose-600]:tw-text-[#dc3545] [&.tw-text-rose-600:hover]:tw-bg-[rgba(220,_53,_69,_0.05)] [&.tw-text-rose-600:hover]:tw-text-[#dc3545] ui-nav-item"}>
                  <button
                    className={`tw-gap-2 tw-rounded-[0.5rem] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-text-[0.875rem] tw-leading-[1.25rem] tw-font-medium [--tw-text-opacity:1] tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:hover]:[--tw-text-opacity:1] [&:hover]:tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [&.active]:[--tw-bg-opacity:1] [&.active]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&.active]:[--tw-text-opacity:1] [&.active]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] ui-nav-link tw-flex tw-items-center   ${activeTab === "assessments" ? "active" : ""}      ${!canViewAssessments ? "disabled" : ""}`}
                    onClick={() => handleTabChange('assessments')}
                    disabled={!canViewAssessments}
                    title={!canViewAssessments ? "You don't have permission to view assessments" : "View Assessments"}
                  >
                    <Icon icon="fluent:quiz-new-24-regular" className="tw-me-2" width="20" height="20" />
                    Assessments
                  </button>
                </li>
                <li className={"tw-rounded-[8px] tw-m-[4px_8px] [font-family:var(--font-primary)] [font-weight:var(--font-weight-medium)] tw-text-[#555] [transition:all_0.2s_ease] tw-no-underline tw-relative [&:hover]:tw-bg-[rgba(13,_110,_253,_0.05)] [&:hover]:tw-text-[#0d6efd] [&.active]:tw-bg-[var(--primary-color)] [&.active]:tw-text-[white] [&.tw-text-rose-600]:tw-text-[#dc3545] [&.tw-text-rose-600:hover]:tw-bg-[rgba(220,_53,_69,_0.05)] [&.tw-text-rose-600:hover]:tw-text-[#dc3545] ui-nav-item"}>
                  <button
                    className={`tw-gap-2 tw-rounded-[0.5rem] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-text-[0.875rem] tw-leading-[1.25rem] tw-font-medium [--tw-text-opacity:1] tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:hover]:[--tw-text-opacity:1] [&:hover]:tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [&.active]:[--tw-bg-opacity:1] [&.active]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&.active]:[--tw-text-opacity:1] [&.active]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] ui-nav-link tw-flex tw-items-center   ${activeTab === "live" ? "active" : ""}      ${!canViewLiveClasses ? "disabled" : ""}`}
                    onClick={() => handleTabChange('live')}
                    disabled={!canViewLiveClasses}
                    title={!canViewLiveClasses ? "You don't have permission to view live classes" : "View Live Classes"}
                  >
                    <Icon icon="fluent:video-person-24-regular" className="tw-me-2" width="20" height="20" />
                    Live Classes
                  </button>
                </li>
                <li className={"tw-rounded-[8px] tw-m-[4px_8px] [font-family:var(--font-primary)] [font-weight:var(--font-weight-medium)] tw-text-[#555] [transition:all_0.2s_ease] tw-no-underline tw-relative [&:hover]:tw-bg-[rgba(13,_110,_253,_0.05)] [&:hover]:tw-text-[#0d6efd] [&.active]:tw-bg-[var(--primary-color)] [&.active]:tw-text-[white] [&.tw-text-rose-600]:tw-text-[#dc3545] [&.tw-text-rose-600:hover]:tw-bg-[rgba(220,_53,_69,_0.05)] [&.tw-text-rose-600:hover]:tw-text-[#dc3545] ui-nav-item"}>
                  <button
                    className={`tw-gap-2 tw-rounded-[0.5rem] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-text-[0.875rem] tw-leading-[1.25rem] tw-font-medium [--tw-text-opacity:1] tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:hover]:[--tw-text-opacity:1] [&:hover]:tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [&.active]:[--tw-bg-opacity:1] [&.active]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&.active]:[--tw-text-opacity:1] [&.active]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] ui-nav-link tw-flex tw-items-center   ${activeTab === "recorded" ? "active" : ""}      ${!canViewResources ? "disabled" : ""}`}
                    onClick={() => handleTabChange('recorded')}
                    disabled={!canViewResources}
                    title={!canViewResources ? "You don't have permission to view resources" : "View Resources"}
                  >
                    <Icon icon="fluent:document-24-regular" className="tw-me-2" width="20" height="20" />
                    Resources
                  </button>
                </li>
                {SHOW_CLASSROOM_COMMUNITY_TAB && (
                  <li className={"tw-rounded-[8px] tw-m-[4px_8px] [font-family:var(--font-primary)] [font-weight:var(--font-weight-medium)] tw-text-[#555] [transition:all_0.2s_ease] tw-no-underline tw-relative [&:hover]:tw-bg-[rgba(13,_110,_253,_0.05)] [&:hover]:tw-text-[#0d6efd] [&.active]:tw-bg-[var(--primary-color)] [&.active]:tw-text-[white] [&.tw-text-rose-600]:tw-text-[#dc3545] [&.tw-text-rose-600:hover]:tw-bg-[rgba(220,_53,_69,_0.05)] [&.tw-text-rose-600:hover]:tw-text-[#dc3545] ui-nav-item"}>
                    <button
                      className={`tw-gap-2 tw-rounded-[0.5rem] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-text-[0.875rem] tw-leading-[1.25rem] tw-font-medium [--tw-text-opacity:1] tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:hover]:[--tw-text-opacity:1] [&:hover]:tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [&.active]:[--tw-bg-opacity:1] [&.active]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&.active]:[--tw-text-opacity:1] [&.active]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] ui-nav-link tw-flex tw-items-center   ${activeTab === "community" ? "active" : ""}      ${!canViewCommunity ? "disabled" : ""}`}
                      onClick={() => handleTabChange('community')}
                      disabled={!canViewCommunity}
                      title={!canViewCommunity ? "You don't have permission to view community" : "View Community"}
                    >
                      <Icon icon="fluent:chat-24-regular" className="tw-me-2" width="20" height="20" />
                      Community
                      {communityUnread > 0 && activeTab !== 'community' && (
                        <span className={"[&.tw-bg-brand-50]:!tw-bg-[#e7f1ff] [&.tw-bg-brand-50]:tw-border-[1px] [&.tw-bg-brand-50]:tw-border-solid [&.tw-bg-brand-50]:tw-border-[#b6d4fe] tw-inline-flex tw-items-center tw-gap-1 tw-pl-2.5 tw-pr-2.5 tw-pt-1 tw-pb-1 tw-text-[0.75rem] tw-font-medium tw-leading-[1rem] ui-badge tw-rounded-full tw-bg-rose-600 tw-ms-2"}>
                          {communityUnread}
                        </span>
                      )}
                    </button>
                  </li>
                )}
                <li className={"tw-rounded-[8px] tw-m-[4px_8px] [font-family:var(--font-primary)] [font-weight:var(--font-weight-medium)] tw-text-[#555] [transition:all_0.2s_ease] tw-no-underline tw-relative [&:hover]:tw-bg-[rgba(13,_110,_253,_0.05)] [&:hover]:tw-text-[#0d6efd] [&.active]:tw-bg-[var(--primary-color)] [&.active]:tw-text-[white] [&.tw-text-rose-600]:tw-text-[#dc3545] [&.tw-text-rose-600:hover]:tw-bg-[rgba(220,_53,_69,_0.05)] [&.tw-text-rose-600:hover]:tw-text-[#dc3545] ui-nav-item"}>
                  <button
                    className={`tw-gap-2 tw-rounded-[0.5rem] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-text-[0.875rem] tw-leading-[1.25rem] tw-font-medium [--tw-text-opacity:1] tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] ui-nav-link tw-flex tw-items-center ${activeTab === "courses" ? "active" : ""} ${!canViewCourses ? "disabled" : ""}`}
                    onClick={() => handleTabChange('courses')}
                    disabled={!canViewCourses}
                    title={!canViewCourses ? "You don't have permission to view courses" : "View Courses"}
                  >
                    <Icon icon="fluent:book-open-24-regular" className="tw-me-2" width="20" height="20" />
                    Courses
                  </button>
                </li>
              </ul>

              <div className="tw-hidden sm:tw-flex tw-items-center tw-gap-4">
                <div className="!tw-text-[var(--secondary-color)] !tw-text-[12px] tw-text-slate-500 tw-text-sm">
                  <span className="tw-me-2">
                    <Icon icon="fluent:eye-24-regular" className="tw-me-1" />
                    {Object.values(tabVisibility).filter(Boolean).length} visible
                  </span>
                  <span>
                    <Icon icon="fluent:eye-off-24-regular" className="tw-me-1" />
                    {Object.values(tabVisibility).filter(v => !v).length} hidden
                  </span>
                </div>
                <div className="tw-relative tw-flex tw-items-center tw-gap-4">
                  <button
                    ref={buttonRef}
                    className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-p-[8px_16px] tw-font-medium [transition:all_0.2s_ease] tw-shrink-[0] tw-justify-center tw-border-[1px] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] tw-rounded-[0.375rem] tw-pl-3 tw-pr-3 tw-pt-1.5 tw-pb-1.5 tw-text-[0.75rem] tw-leading-[1rem] ui-button ui-button-outline-secondary ui-button-sm tw-flex tw-items-center tw-gap-2"}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsTabSettingsOpen(!isTabSettingsOpen);
                    }}
                  >
                    <Icon icon="fluent:settings-24-regular" width="20" height="20" />
                    Tab Settings
                  </button>
                  {isTabSettingsOpen && <TabSettings />}
                </div>
              </div>

            </div>
          </div>

          {/* Tab Content */}
          <div className={"max-[768px]:tw-p-[2px] tw-p-[15px] tw-bg-[#fff] [&_>_.tab-pane]:tw-hidden [&_>_.tab-pane.active]:tw-block tab-content"}>
            {linkedContentStatus === 'loading' ? (
              <div className="tw-p-6">
                <Loader variant="section" caption="Opening content from your notification..." />
              </div>
            ) : linkedContentStatus === 'unavailable' ? (
              <div className="tw-p-6">
                <ContentUnavailable
                  title={getLinkedUnavailableContent().title}
                  description="It may have been removed, cancelled, or deleted from this classroom."
                  icon={getLinkedUnavailableContent().icon}
                  backLabel="View Classroom"
                  onBack={handleDismissLinkedContent}
                />
              </div>
            ) : (
              <>
            {activeTab === 'assignments' && canViewAssignments && (
              <div className="assignments">
                <ClassroomAssignments key={`assignments-${tabRefreshKey}`} decodedClassroomId={decodedClassroomId} />
              </div>
            )}

            {activeTab === 'courses' && canViewCourses && (
              <ClassroomCourses key={`courses-${tabRefreshKey}`} classroomId={decodedClassroomId} />
            )}

            {activeTab === 'assessments' && canViewAssessments && (
              <div className="assessments">
                <ClassroomAssessments key={`assessments-${tabRefreshKey}`} decodedClassroomId={decodedClassroomId} />
              </div>
            )}

            {activeTab === 'live' && canViewLiveClasses && (
              <div className="live-classes">
                <ClassroomLiveClasses key={`live-${tabRefreshKey}`} decodedClassroomId={decodedClassroomId} />
              </div>
            )}

            {activeTab === 'recorded' && canViewResources && (
              <div className="recorded">
                <ClassroomRecorded key={`recorded-${tabRefreshKey}`} decodedClassroomId={decodedClassroomId} />
              </div>
            )}

            {SHOW_CLASSROOM_COMMUNITY_TAB &&
              activeTab === 'community' &&
              canViewCommunity && (
              <div className="community">
                <ClassroomCommunity
                  key={`community-${tabRefreshKey}`}
                  classroom_id={decodedClassroomId}
                  stream_channel_id={dashboardData?.stream_channel_id}
                  group_id={groupData?.group_id}
                  group_name={groupData?.group_name}
                />
              </div>
            )}

            {/* Show message when user doesn't have permission for current tab */}
            {((activeTab === 'assignments' && !canViewAssignments) ||
              (activeTab === 'courses' && !canViewCourses) ||
              (activeTab === 'assessments' && !canViewAssessments) ||
              (activeTab === 'live' && !canViewLiveClasses) ||
              (activeTab === 'recorded' && !canViewResources) ||
              (SHOW_CLASSROOM_COMMUNITY_TAB &&
                activeTab === 'community' &&
                !canViewCommunity)) && (
              <div className="tw-text-center tw-py-12">
                <Icon icon="fluent:error-circle-24-regular" className="!tw-text-[var(--danger-color)] tw-text-rose-600 tw-mb-4" width="48" height="48" />
                <h5>Access Denied</h5>
                <p className="!tw-text-[var(--secondary-color)] tw-text-slate-500">You don't have permission to view this content.</p>
              </div>
            )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default ClassroomDashboard
